import { useEffect, useRef, useState } from "react";
import { SavePostView } from "../../apis/posts/save_post_view";
import { InView } from "react-intersection-observer";

export default function MediaRender({ item, setVmute, vmute }) {
    const mediaItems = item?.media_items || [];
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);

    const videoRefs = useRef({});
    const isAd = item?.ad_post === 1;

    const handleViewChange = (inView) => {
        setIsPlaying(inView);
        if (inView && item?.p_id) {
            SavePostView({ p_id: item.p_id }).catch(() => { });
        }
    };

    useEffect(() => {
        Object.keys(videoRefs.current).forEach((key) => {
            const index = parseInt(key, 10);
            const videoEl = videoRefs.current[index];
            if (videoEl) {
                if (isPlaying && index === currentIndex) {
                    videoEl.play().catch(() => { });
                } else {
                    videoEl.pause();
                }
            }
        });
    }, [isPlaying, currentIndex]);

    if (!mediaItems.length) return null;

    const prevSlide = (e) => {
        e.stopPropagation();
        setCurrentIndex((prev) => (prev === 0 ? prev : prev - 1));
    };

    const nextSlide = (e) => {
        e.stopPropagation();
        setCurrentIndex((prev) => (prev === mediaItems.length - 1 ? prev : prev + 1));
    };

    return (
        <InView threshold={0.6} onChange={handleViewChange}>
            {({ ref }) => (
                <div ref={ref} className="relative w-full bg-black overflow-hidden select-none">

                    {/* Counter Badge */}
                    {mediaItems.length > 1 && (
                        <div className="absolute top-3 right-3 bg-black/60 text-white text-xs px-2.5 py-1 rounded-full backdrop-blur-md z-20 font-medium pointer-events-none">
                            {currentIndex + 1}/{mediaItems.length}
                        </div>
                    )}

                    {/* Carousel Track with UIkit Lightbox Attribute */}
                    <div
                        uk-lightbox="animation: slide"
                        className="flex w-full transition-transform duration-300 ease-out"
                        style={{ transform: `translateX(-${currentIndex * 100}%)` }}
                    >
                        {mediaItems.map((media, idx) => {
                            const isVideo = media.file_type === "video" || media.file_type === "videos";
                            const mediaSrc = media.file_path || media.thumbnail;
                            const isAdImage = isAd && !isVideo;

                            return (
                                <div
                                    key={media.file_id || idx}
                                    className="min-w-full w-full flex-shrink-0 relative bg-black flex items-center justify-center overflow-hidden"
                                >
                                    {isVideo ? (
                                        mediaSrc ? (
                                            <div className="relative w-full h-full flex items-center justify-center">
                                                <video
                                                    ref={(el) => (videoRefs.current[idx] = el)}
                                                    src={mediaSrc}
                                                    poster={media.thumbnail}
                                                    loop
                                                    muted={vmute}
                                                    playsInline
                                                    className="w-full h-auto max-h-[600px] object-contain cursor-pointer"
                                                    onClick={() => setVmute(!vmute)}
                                                />

                                                {/* Sound Toggle Overlay */}
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setVmute(!vmute);
                                                    }}
                                                    className={`absolute ${isAd ? 'bottom-6' : 'bottom-3'} right-3 bg-black/60 text-white p-2 rounded-full backdrop-blur-md transition hover:bg-black/80 z-20`}
                                                >
                                                    {vmute ? (
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
                                                        </svg>
                                                    ) : (
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                                                        </svg>
                                                    )}
                                                </button>
                                            </div>
                                        ) : (
                                            <a href={media.thumbnail} data-caption={item.p_title || ""}>
                                                <img
                                                    src={media.thumbnail}
                                                    alt={item.p_title || "Media Post"}
                                                    className="w-full h-auto max-h-[600px] object-contain cursor-pointer"
                                                />
                                            </a>
                                        )
                                    ) : (
                                        <a
                                            href={mediaSrc}
                                            data-caption={item.p_title || ""}
                                            className="w-full block"
                                        >
                                            <img
                                                src={mediaSrc}
                                                alt={item.p_title || "Media Post"}
                                                className={
                                                    isAdImage
                                                        ? "w-full object-cover cursor-pointer"
                                                        : "w-full h-auto max-h-[600px] object-contain cursor-pointer"
                                                }
                                                style={isAdImage ? { aspectRatio: "1 / 1" } : {}}
                                                loading="lazy"
                                            />
                                        </a>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {/* Prev Button */}
                    {mediaItems.length > 1 && currentIndex > 0 && (
                        <button
                            type="button"
                            onClick={prevSlide}
                            className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/60 text-white p-1.5 rounded-full backdrop-blur-md transition hover:bg-black/80 z-20"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
                            </svg>
                        </button>
                    )}

                    {/* Next Button */}
                    {mediaItems.length > 1 && currentIndex < mediaItems.length - 1 && (
                        <button
                            type="button"
                            onClick={nextSlide}
                            className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/60 text-white p-1.5 rounded-full backdrop-blur-md transition hover:bg-black/80 z-20"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                            </svg>
                        </button>
                    )}

                    {/* Dots Indicator */}
                    {mediaItems.length > 1 && (
                        <div className={`absolute left-0 right-0 flex justify-center gap-1.5 z-20 pointer-events-none ${isAd && item.ad_post_link ? "bottom-10" : "bottom-3"
                            }`}>
                            {mediaItems.map((_, idx) => (
                                <span
                                    key={idx}
                                    className={`h-1.5 rounded-full transition-all duration-300 ${idx === currentIndex ? "w-4 bg-blue-500" : "w-1.5 bg-white/60"
                                        }`}
                                />
                            ))}
                        </div>
                    )}

                    {/* Ad CTA Bar */}
                    {isAd && item.ad_post_link && (
                        <a
                            href={item.ad_post_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="absolute bottom-0 left-0 right-0 bg-blue-600 text-white py-2 px-4 flex items-center justify-between text-xs font-bold uppercase tracking-wide hover:bg-blue-700 transition z-20"
                        >
                            <span>{item.ad_post_type === "buy" ? "Buy Now" : "Visit Website"}</span>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                        </a>
                    )}
                </div>
            )}
        </InView>
    );
}